import styled from 'styled-components'
import { useStore } from '../pageView/Page'
import useTranslation from '../../hooks/useTranslation'

// Read-only route panel shown in the warehouseOrder card's Route tab. Renders
// the sequenced routeStops so the plan is visible before the order is released.
// Cross-warehouse orders group stops under a per-warehouse header and render the
// `travel` hand-off between warehouses as a distinct divider row.
const Panel = styled.div`
    background: ${({ theme }) => theme.card.background};
    border: 1px solid ${({ theme }) => theme.form.border};
    border-radius: 1rem;
    padding: 1rem;
`

const Empty = styled.div`
    text-align: center;
    padding: 2rem 1rem;
    color: ${({ theme }) => theme.font.label};
    font-size: 0.9rem;
`

const Row = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 0.6rem;
    padding: 0.5rem 0;
    border-top: 1px solid ${({ theme }) => theme.form.border};
`

const Seq = styled.span`
    flex: 0 0 1.6rem;
    text-align: center;
    font-weight: 700;
    line-height: 1.4;
    color: ${({ theme }) => theme.font.label};
`

const Action = styled.span`
    flex: 0 0 3.2rem;
    font-size: 0.72rem;
    font-weight: 700;
    line-height: 1.4;
    text-transform: uppercase;
    color: ${({ theme, $action }) => $action === 'drop' ? theme.btn.secondary : theme.btn.primary};
`

const Body = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
`

const Loc = styled.span`
    font-weight: 600;
    font-size: 0.9rem;
    font-variant-numeric: tabular-nums;
    color: ${({ theme }) => theme.font.color};
    word-break: break-word;
`

const Summary = styled.span`
    font-size: 0.8rem;
    color: ${({ theme }) => theme.font.label};
`

const WarehouseHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.6rem;
    padding: 0.3rem 0.1rem;
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${({ theme }) => theme.font.label};

    &:first-child { margin-top: 0; }
`

const TravelRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.5rem 0.6rem;
    margin: 0.5rem 0;
    border: 1px dashed ${({ theme }) => theme.form.border};
    border-radius: 0.6rem;
    background: ${({ theme }) => theme.form.background};
    font-size: 0.8rem;
    font-weight: 600;
    color: ${({ theme }) => theme.font.color};
`

const TravelDistance = styled.span`
    flex: 1;
    text-align: right;
    font-weight: 500;
    color: ${({ theme }) => theme.font.label};
`

const WarehouseOrderRoute = () => {
    const { t } = useTranslation()
    const [routeStops] = useStore(s => s.routeStops)

    if (!Array.isArray(routeStops) || routeStops.length === 0) {
        return <Empty>{t('warehouseOrder.route.empty')}</Empty>
    }

    // Only show warehouse headers when the route actually spans more than one.
    const multiWarehouse = new Set(
        routeStops.filter(s => s.action !== 'travel' && s.warehouseId).map(s => s.warehouseId)
    ).size > 1

    let lastHeaderWh = null

    return (
        <Panel>
            {routeStops.map((stop, i) => {
                if (stop.action === 'travel') {
                    lastHeaderWh = null // force a fresh header for the next warehouse's stops
                    return (
                        <TravelRow key={i}>
                            <span>{t('warehouseOrder.route.travel')}</span>
                            <span>{stop.summary}</span>
                            <TravelDistance>{stop.actionLabel}</TravelDistance>
                        </TravelRow>
                    )
                }

                const showHeader = multiWarehouse && stop.warehouseId && stop.warehouseId !== lastHeaderWh
                if (showHeader) lastHeaderWh = stop.warehouseId

                return (
                    <div key={i}>
                        {showHeader && (
                            <WarehouseHeader>
                                {t('warehouseOrder.route.warehouse')} {stop.warehouseCode || stop.warehouseId}
                            </WarehouseHeader>
                        )}
                        <Row>
                            <Seq>{stop.seq}</Seq>
                            <Action $action={stop.action}>{stop.actionLabel}</Action>
                            <Body>
                                <Loc>{stop.locationId || stop.position}</Loc>
                                {stop.summary && <Summary>{stop.summary}</Summary>}
                            </Body>
                        </Row>
                    </div>
                )
            })}
        </Panel>
    )
}

export default WarehouseOrderRoute
